import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import "@/App.css";

// Pages
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Auth/Login";
import Register from "@/pages/Auth/Register";
import ForgotPassword from "@/pages/Auth/ForgotPassword";
import SetPassword from "@/pages/Auth/SetPassword";
import AuthCallback from "@/pages/Auth/AuthCallback";
import ReferralRedirect from "@/pages/Auth/ReferralRedirect";
import Search from "@/pages/Search";
import ProductDetail from "@/pages/ProductDetail";
import CompanyProfile from "@/pages/CompanyProfile";

// Help Pages
import ExcelTutorial from "@/pages/Help/ExcelTutorial";

// Surplus Pages
import SurplusList from "@/pages/Surplus/SurplusList";
import SurplusDetail from "@/pages/Surplus/SurplusDetail";

// Dashboard Layout & Pages
import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardHome from "@/pages/Dashboard/DashboardHome";
import CompanyForm from "@/pages/Dashboard/CompanyForm";
import ProductList from "@/pages/Dashboard/ProductList";
import ProductForm from "@/pages/Dashboard/ProductForm";
import QuoteList from "@/pages/Dashboard/QuoteList";
import QuoteDetail from "@/pages/Dashboard/QuoteDetail";
import SampleList from "@/pages/Dashboard/SampleList";
import SampleDetail from "@/pages/Dashboard/SampleDetail";
import ImportList from "@/pages/Dashboard/ImportList";
import ImportForm from "@/pages/Dashboard/ImportForm";
import MessageList from "@/pages/Dashboard/MessageList";
import SubscriptionPage from "@/pages/Dashboard/SubscriptionPage";
import TexVisionPage from "@/pages/Dashboard/TexVisionPage";
import NotificationList from "@/pages/Dashboard/NotificationList";
import NotificationPreferences from "@/pages/Dashboard/NotificationPreferences";
import FavoritesPage from "@/pages/Dashboard/FavoritesPage";
import SurplusForm from "@/pages/Dashboard/SurplusForm";
import MySurplus from "@/pages/Dashboard/MySurplus";
import ProvidersPage from "@/pages/Dashboard/ProvidersPage";
import BuyersPage from "@/pages/Dashboard/BuyersPage";
import OrderList from "@/pages/Dashboard/OrderList";
import OrderDetail from "@/pages/Dashboard/OrderDetail";
import MyStoreOrders from "@/pages/Dashboard/MyStoreOrders";
import AdminPanel from "@/pages/Admin/AdminPanel";
import CompanySettingsPage from "@/pages/Dashboard/CompanySettingsPage";
import TeamPage from "@/pages/Dashboard/TeamPage";
import CommunityBlogPage from "@/pages/Dashboard/CommunityBlogPage";
import SponsorsPage from "@/pages/Dashboard/SponsorsPage";
import InventoryUpload from "@/pages/Dashboard/InventoryUpload";
import AnalyticsPage from "@/pages/Dashboard/AnalyticsPage";
import FixAccount from "@/pages/Admin/FixAccount";
import AdminUsersPage from "@/pages/Admin/AdminUsersPage";
import SharedCatalogPage from "@/pages/SharedCatalogPage";
import JoinTeamPage from "@/pages/JoinTeamPage";
import BetaTestingPage from "@/pages/BetaTestingPage";
import TermsPage from "@/pages/Legal/TermsPage";
import PrivacyPage from "@/pages/Legal/PrivacyPage";
import CancellationPage from "@/pages/Legal/CancellationPage";

// Store Pages
import MyStorePage from "@/pages/Store/MyStorePage";
import PublicStorePage from "@/pages/Store/PublicStorePage";
import StoresDirectory from "@/pages/Store/StoresDirectory";

// Onboarding
import OnboardingFlow from "@/components/OnboardingFlow";
import SellerOnboarding from "@/pages/Onboarding/SellerOnboarding";

// Auth Callback Handler Component
const AuthCallbackHandler = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return null;
};

// Auto-login component for preview mode
const AutoLogin = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tried, setTried] = React.useState(false);
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const autoLogin = params.get('autologin');
    
    // DISABLED: Auto-login feature removed for security
    // Testers should use the /beta page to get login credentials
    // Only enable for development/debugging if needed
    if (false && autoLogin === 'admin' && !isAuthenticated && !loading && !tried && !isLoggingIn) {
      setTried(true);
      setIsLoggingIn(true);
      console.log('Auto-login disabled for security');
    }
  }, [isAuthenticated, loading, location.search, login, navigate, tried, isLoggingIn]);
  
  // Show loading while auto-login is in progress
  if (isLoggingIn && !isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Iniciando sesión...</p>
        </div>
      </div>
    );
  }
  
  return null;
};

function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback - must be done synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/precios" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/establecer-contrasena" element={<SetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/beta" element={<BetaTestingPage />} />
      <Route path="/search" element={<Search />} />
      <Route path="/products/:productId" element={<ProductDetail />} />
      <Route path="/company/:companyId" element={<CompanyProfile />} />
      <Route path="/surplus" element={<SurplusList />} />
      <Route path="/surplus/:surplusId" element={<SurplusDetail />} />
      <Route path="/shared/:token" element={<SharedCatalogPage />} />
      <Route path="/join/:token" element={<JoinTeamPage />} />
      <Route path="/ref/:code" element={<ReferralRedirect />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cancellation" element={<CancellationPage />} />
      
      {/* Onboarding */}
      <Route path="/onboarding" element={<OnboardingFlow />} />
      <Route path="/seller-onboarding" element={<SellerOnboarding />} />
      
      {/* Public Store */}
      <Route path="/tienda/:slug" element={<PublicStorePage />} />
      <Route path="/tiendas" element={<StoresDirectory />} />

      {/* Help / Tutorial Pages */}
      <Route path="/help/excel" element={<ExcelTutorial />} />
      <Route path="/ayuda/excel" element={<ExcelTutorial />} />

      {/* Dashboard Routes */}
      <Route path="/dashboard" element={<DashboardLayout />}>
        <Route index element={<DashboardHome />} />
        <Route path="providers" element={<ProvidersPage />} />
        <Route path="buyers" element={<BuyersPage />} />
        <Route path="company" element={<CompanyForm />} />
        <Route path="company/new" element={<CompanyForm isNew />} />
        <Route path="products" element={<ProductList />} />
        <Route path="products/new" element={<ProductForm isNew />} />
        <Route path="products/:productId/edit" element={<ProductForm />} />
        <Route path="inventory/upload" element={<InventoryUpload />} />
        <Route path="quotes" element={<QuoteList />} />
        <Route path="quotes/:quoteId" element={<QuoteDetail />} />
        <Route path="samples" element={<SampleList />} />
        <Route path="samples/:sampleId" element={<SampleDetail />} />
        <Route path="imports" element={<ImportList />} />
        <Route path="imports/new" element={<ImportForm />} />
        <Route path="messages" element={<MessageList />} />
        <Route path="messages/:conversationId" element={<MessageList />} />
        <Route path="orders" element={<OrderList />} />
        <Route path="orders/:orderId" element={<OrderDetail />} />
        <Route path="store-orders" element={<MyStoreOrders />} />
        <Route path="saved" element={<FavoritesPage />} />
        <Route path="settings" element={<CompanySettingsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="blog" element={<CommunityBlogPage />} />
        <Route path="sponsors" element={<SponsorsPage />} />
        <Route path="surplus" element={<MySurplus />} />
        <Route path="surplus/new" element={<SurplusForm isNew={true} />} />
        <Route path="surplus/:surplusId/edit" element={<SurplusForm isNew={false} />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="texvision" element={<TexVisionPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="notifications" element={<NotificationList />} />
        <Route path="notifications/preferences" element={<NotificationPreferences />} />
        <Route path="store" element={<MyStorePage />} />
        <Route path="admin" element={<AdminPanel />} />
        <Route path="admin/users" element={<AdminUsersPage />} />
        <Route path="fix-account" element={<FixAccount />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <LocaleProvider>
      <AuthProvider>
        <BrowserRouter>
          <AutoLogin />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LocaleProvider>
  );
}

export default App;
